/**
 * @fileoverview The presigned-URL upload pipeline for one queued recording
 * (iOS review B6 moved it out of the recorder so the Saved recordings page
 * can send a take too).
 *
 * start-upload → S3 PUT (multipart over 5MB, else single) → confirm. The
 * confirmation receipt is kept in `recordingUploadSession` as soon as the
 * server names an upload generation, so a retry after a lost confirm posts
 * the same receipt again rather than uploading a second generation.
 */
import axios from "axios";
import api from "@/lib/api";
import { recordingUploadSession } from "@/lib/recordingUploadSession";
import { selectionPayload } from "@/lib/schedulingView";

/**
 * Run the upload pipeline for a queue entry. Resolves to the lesson id;
 * throws on any failure so the caller can persist and retry. A confirm that
 * fails after the bytes landed carries `confirmFailed` and `lessonId`, so
 * the reason reads as "processing didn’t start" rather than a lost upload.
 *
 * @param {Object} entry - A queue entry: kind, blob, contentType, duration, title, instrument, ...
 * @param {(pct: number) => void} [onProgress] - Receives 0–100.
 * @returns {Promise<string>} The lesson id.
 */
export async function performUpload(entry, onProgress) {
  const { kind, blob, contentType, duration: dur, title, instrument } = entry;
  const fileSize = blob.size;

  const baseBody = { title, duration_seconds: dur, content_type: contentType, file_size: fileSize, upload_protocol: 2 };
  if (instrument && instrument !== "auto") baseBody.instrument = instrument;

  let startData;
  const savedSession = recordingUploadSession.read(entry.id);
  if (savedSession?.confirmation) {
    await confirm(savedSession.confirmUrl, savedSession.confirmation, savedSession.lesson_id);
    return savedSession.lesson_id;
  }
  if (savedSession) {
    const prefix = kind === "class" ? "class-lessons" : "lessons";
    ({ data: startData } = await api.post(`/${prefix}/${savedSession.lesson_id}/resume-upload`, {
      upload_generation: savedSession.upload_generation, s3_key: savedSession.s3_key,
    }));
    if (startData.confirmed) return startData.lesson_id;
  } else if (entry.rerecordLessonId) {
    // Re-record: reset the existing lesson in place and reuse its id. The
    // student/class + scheduling stamp are fixed by the lesson server-side,
    // so only the new capture's shape is sent.
    const url =
      kind === "class"
        ? `/class-lessons/${entry.rerecordLessonId}/rerecord-upload`
        : `/lessons/${entry.rerecordLessonId}/rerecord-upload`;
    ({ data: startData } = await api.post(url, baseBody));
  } else if (kind === "class") {
    const payload = { ...baseBody, ...selectionPayload(entry.scheduledSelection ||
      (entry.scheduledSlotId ? { slotId: entry.scheduledSlotId, date: entry.scheduledDate } : null)) };
    ({ data: startData } = await api.post(`/classes/${entry.classId}/lessons/start-upload`, payload));
  } else {
    const payload = { student_id: entry.studentId, ...baseBody, ...selectionPayload(entry.scheduledSelection ||
      (entry.scheduledSlotId ? { slotId: entry.scheduledSlotId, date: entry.scheduledDate } : null)) };
    ({ data: startData } = await api.post("/lessons/start-upload", payload));
  }
  const { lesson_id } = startData;
  const identity = startData.upload_generation ? {
    lesson_id, upload_generation: startData.upload_generation, s3_key: startData.s3_key,
  } : null;
  if (identity) recordingUploadSession.save(entry.id, identity);

  // Report progress from a byte count rather than from the event directly:
  // the bar must be able to advance on facts we know independently of the
  // browser's optional progress events.
  const report = (bytes) => {
    if (!fileSize) return;
    onProgress?.(Math.min(100, Math.round((bytes / fileSize) * 100)));
  };

  let completeParts = null;
  if (startData.multipart) {
    const { part_urls, chunk_size } = startData;
    const parts = [];
    for (let i = 0; i < part_urls.length; i++) {
      const start = i * chunk_size;
      const end = Math.min(start + chunk_size, fileSize);
      const chunk = blob.slice(start, end);
      const response = await axios.put(part_urls[i], chunk, {
        onUploadProgress: (e) => {
          // Weight progress by bytes, not part count — the last part is
          // usually smaller than chunk_size.
          if (e.loaded != null) report(start + e.loaded);
        },
      });
      // Reading the ETag cross-origin requires the bucket CORS config to
      // list it in ExposeHeaders (axios lowercases header names).
      const etag = response.headers.etag;
      if (!etag) throw new Error(`Multipart upload part ${i + 1} failed: missing ETag`);
      parts.push({ PartNumber: i + 1, ETag: etag });
      // Floor the bar at the bytes we know landed. `onUploadProgress` is
      // best-effort — WebKit (so every iOS browser) routinely reports
      // nothing at all for a cross-origin PUT — and this loop used to take
      // its ONLY movement from those events, so a big lesson on a slow link
      // sat at 0% for the entire upload and then jumped straight to done.
      // A completed part is proof, so the bar advances on it either way.
      report(end);
    }
    completeParts = parts;
  } else {
    const putResponse = await axios.put(startData.upload_url, blob, {
      headers: { "Content-Type": contentType },
      onUploadProgress: (e) => {
        // Ratio off the event's own total, which is the body size the
        // browser actually sees, rather than assuming it equals blob.size.
        if (e.total) onProgress?.(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      },
    });
    // Guard against a non-2xx that didn't throw, so we never confirm an
    // upload that didn't actually land in S3.
    if (putResponse.status < 200 || putResponse.status >= 300) {
      throw new Error(`Upload failed: S3 returned ${putResponse.status}`);
    }
    // Same reasoning as the per-part floor: a resolved PUT is proof the
    // bytes landed, whether or not a single progress event ever fired.
    report(fileSize);
  }

  const confirmBase = kind === "class" ? `/class-lessons/${lesson_id}` : `/lessons/${lesson_id}`;
  const generation = identity ? { upload_generation: identity.upload_generation, s3_key: identity.s3_key } : {};
  const confirmBody = startData.multipart
    ? { upload_id: startData.upload_id, parts: completeParts, ...generation }
    : identity ? generation : undefined;
  const confirmUrl = `${confirmBase}/${startData.multipart ? "complete-multipart-upload" : "confirm-upload"}`;
  if (identity) recordingUploadSession.save(entry.id, { ...identity, confirmUrl, confirmation: confirmBody });
  await confirm(confirmUrl, confirmBody, lesson_id);
  return lesson_id;
}

/**
 * Post the confirmation. The bytes are in S3 by now, so a failure here is
 * "processing didn’t start", not a lost upload: the error is marked so the
 * saved reason says so and a retry sends the same receipt again.
 */
async function confirm(url, body, lessonId) {
  try {
    if (body === undefined) await api.post(url);
    else await api.post(url, body);
  } catch (error) {
    if (error && typeof error === "object") {
      error.confirmFailed = true;
      error.lessonId = lessonId;
    }
    throw error;
  }
}
